/**
 * CombinedAccountsWidget — Two-group widget: Activity (Earned + Spend) + Accounts
 *
 * Group 1 (Activity): white card, rounded top, flat bottom
 * 8px cream gap
 * Group 2 (Accounts): white card, flat top, rounded bottom
 * SyncRow below
 *
 * Replaces separate ActivityCard + AccountsAccordion on Dashboard.
 */

import { T } from '@/styles/tokens';
import { ActivityCard } from '@/components/money/ActivityCard';
import { AccountsAccordion, type PlaidAccount } from '@/components/money/AccountsAccordion';
import { SyncRow } from '@/components/money/SyncRow';
import type { ArtTransaction } from '@/hooks/useArtTransactions';
import type { DashboardMode } from '@/components/money/ActivityZones';

interface CombinedAccountsWidgetProps {
  // Activity data
  transactions: ArtTransaction[];
  mode: DashboardMode;
  unlimitedTotals: {
    materialsCents: number;
    incomeCents: number;
    overheadCents: number;
    personalCents: number;
  };
  verdictTitleMap: Record<string, string>;
  animateKey: number;
  /** UNCERTAIN transactions — NOT date-filtered */
  uncertainTransactions?: ArtTransaction[];
  /** Pool 2: sold verdicts awaiting bank payment */
  reportedVerdicts?: any[];
  matchSuggestions?: any[];
  onLinkMatch?: (verdictId: string, txnId: string) => void;
  onDismissMatch?: (suggestionId: string) => void;

  // Account data
  accounts: PlaidAccount[];

  // Sync
  lastSyncAt: number | null;

  // Optional review section rendered between Activity and Accounts
  reviewSection?: React.ReactNode;

  // Callbacks
  onViewAll: (category: string) => void;
  onLinkIncome: (txn: ArtTransaction) => void;
  onTapTransaction: (txn: ArtTransaction) => void;
  onConnectAnother: () => void;
  onSync: () => Promise<void>;
}

export function CombinedAccountsWidget({
  transactions,
  mode,
  unlimitedTotals,
  verdictTitleMap,
  animateKey,
  uncertainTransactions,
  reportedVerdicts,
  matchSuggestions,
  onLinkMatch,
  onDismissMatch,
  reviewSection,
  accounts,
  lastSyncAt,
  onViewAll,
  onLinkIncome,
  onTapTransaction,
  onConnectAnother,
  onSync,
}: CombinedAccountsWidgetProps) {
  return (
    <div>
      {/* Group 1 — Activity */}
      <div style={{
        background: T.white,
        borderRadius: '0 0 14px 14px',
        overflow: 'hidden',
        boxShadow: T.shadowRaised,
      }}>
        <ActivityCard
          transactions={transactions}
          uncertainTransactions={uncertainTransactions}
          onViewAll={onViewAll}
          onLinkIncome={onLinkIncome}
          onTapTransaction={onTapTransaction}
          animateKey={animateKey}
          verdictTitleMap={verdictTitleMap}
          mode={mode}
          reportedVerdicts={reportedVerdicts}
          matchSuggestions={matchSuggestions}
          onLinkMatch={onLinkMatch}
          onDismissMatch={onDismissMatch}
          unlimitedTotals={unlimitedTotals}
        />
      </div>

      {/* Review section slot — between Activity and Accounts */}
      {reviewSection}

      {/* Section label + gap between activity and accounts */}
      <p style={{
        fontSize: 11,
        fontWeight: 700,
        letterSpacing: '1.2px',
        color: T.textMuted,
        textTransform: 'uppercase' as const,
        marginBottom: 8,
        marginTop: 32,
        fontFamily: T.fontFamily,
      }}>
        Accounts
      </p>

      {/* Group 2 — Accounts */}
      <div style={{
        background: T.white,
        borderRadius: 14,
        overflow: 'hidden',
        boxShadow: T.shadowRaised,
      }}>
        <AccountsAccordion
          accounts={accounts}
          onConnectAnother={onConnectAnother}
        />
      </div>

      {/* SyncRow */}
      <SyncRow lastSyncAt={lastSyncAt} onSync={onSync} />
    </div>
  );
}
